import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateRevisionLang } from '@/features/exam-revision/hooks/useCreateRevisionLang';
import { useRevisionDetail } from '@/features/exam-revision/hooks/useRevisionDetail';
import { useRevisionLangs } from '@/features/exam-revision/hooks/useRevisionLangs';
import { useToggleRevisionLangActive } from '@/features/exam-revision/hooks/useToggleRevisionLangActive';
import { useUpdateRevisionLang } from '@/features/exam-revision/hooks/useUpdateRevisionLang';
import type { RevisionLang } from '@/features/exam-revision/types/revision';
import { ConfirmDialog } from '@/features/exam-revision/ui/organisms/ConfirmDialog';
import { RevisionLangsTemplate } from '@/features/exam-revision/ui/templates/RevisionLangsTemplate';
import type { LanguageFormPayload } from '@/shared/ui/organisms/LanguageForm';

export const Route = createFileRoute('/_authenticated/exam/revision/$examId/languages/')({
  component: RevisionLangsPage,
});

export function RevisionLangsPage() {
  const { examId } = Route.useParams();
  const botExamId = Number(examId);
  const navigate = useNavigate();
  const { t } = useTranslation('examRevision');

  const exam = useRevisionDetail(botExamId);
  const { langs, availableLangs, isLoading, isError, refetch } = useRevisionLangs(botExamId);

  const { createLang, isLoading: isCreating } = useCreateRevisionLang(botExamId);
  const { updateLang, isLoading: isUpdating } = useUpdateRevisionLang(botExamId);
  const { toggleActive, isLoading: isToggling } = useToggleRevisionLangActive(botExamId);

  const [deactivateTarget, setDeactivateTarget] = useState<RevisionLang | null>(null);

  const handleTranslate = (lang: RevisionLang) => {
    void navigate({
      to: '/exam/revision/$examId/languages/$langId/translate' as never,
      params: { examId, langId: String(lang.id) } as never,
    });
  };

  const handleSave = (
    payload: LanguageFormPayload,
    editingLangId: number | null,
    callbacks: { onSuccess: () => void },
  ) => {
    if (editingLangId === null) {
      createLang(
        {
          botExamId,
          cultureInfo: payload.cultureInfo,
          title: payload.title ?? null,
          instruction: payload.instruction,
          active: payload.active,
        },
        callbacks,
      );
      return;
    }

    updateLang(
      editingLangId,
      {
        cultureInfo: payload.cultureInfo,
        title: payload.title ?? null,
        instruction: payload.instruction,
        active: payload.active,
      },
      callbacks,
    );
  };

  const handleToggleActive = (lang: RevisionLang, nextActive: boolean) => {
    // Ativar é direto; inativar propaga em cascata, então pede confirmação.
    if (nextActive) {
      toggleActive(lang.id, true);
      return;
    }
    setDeactivateTarget(lang);
  };

  const handleConfirmDeactivate = () => {
    if (!deactivateTarget) return;
    toggleActive(deactivateTarget.id, false, { onSuccess: () => setDeactivateTarget(null) });
  };

  return (
    <>
      <RevisionLangsTemplate
        exam={exam}
        langs={langs}
        isLoading={isLoading}
        isError={isError}
        onRetry={refetch}
        isSaving={isCreating || isUpdating}
        availableLangs={availableLangs}
        onSave={handleSave}
        onTranslate={handleTranslate}
        onToggleActive={handleToggleActive}
      />

      <ConfirmDialog
        open={deactivateTarget !== null}
        title={t('langs.deactivateModal.title')}
        body={t('langs.deactivateModal.body', {
          language: deactivateTarget?.cultureInfoDisplay ?? '',
        })}
        confirmLabel={t('langs.deactivateModal.confirm')}
        cancelLabel={t('langs.deactivateModal.cancel')}
        isLoading={isToggling}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={handleConfirmDeactivate}
      />
    </>
  );
}
